import React, { useEffect, useState } from "react";
import {
  TextField,
  Typography,
  Button,
  Chip,
  InputLabel,
  Box,
  LinearProgress,
  Tabs,
  Tab,
} from "@mui/material";
import RadioButtonsGroup from "../../../Component/UI/RadioGroup/RadioGroup";
import TextInput from "../../../Component/UI/TextInput/TextInput";
import { useSelector } from "react-redux";
import { getPromptForBrandCompetitorsData } from "../../../utils/helpers";
import { fetchGpt4API } from "../../../services/apiService";
import json5 from "json5";
import InputSelectionComponent from "../../Dashboard/Component/inputSelection";
import { ENTITY_LABELS } from "../../../utils/labelMapping";

const radioOption = [
  {
    value: "manual",
    label: "Enter Competitors Manually",
  },
  {
    value: "ai",
    label: "Add Competitors from AI Suggestion",
  },
];
const BrandForm = ({
  brandName,
  setBrandName,
  classes,
  handleCloseModal,
  coreAttributes,
  createNewBrand,
  isUpdateModalOpen,
  updateCreatedBrand,
}) => {
  const personas = useSelector((state) => state.personas);

  const personasData = personas.data.data;

  const [prevBrandName, setPrevBrandName] = useState("");

  // new states

  const [brandData, setBrandData] = useState({
    brandName,
    categories: [], // <-- dynamic categories array
  });

  const [newCategory, setNewCategory] = useState({
    name: "",
    description: "",
  });

  const [currentCategories, setCurrentCategories] = useState([]);

  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);

  const handleCompetitorToggle = (competitor) => {
    if (
      brandData.categories?.[selectedCategoryIndex]?.competitors?.includes(
        competitor
      )
    ) {
      const updated = [...brandData.categories];

      const updatedComps = [
        ...updated[selectedCategoryIndex].competitors,
      ].filter((c) => c !== competitor);

      updated[selectedCategoryIndex] = {
        ...updated[selectedCategoryIndex],
        competitors: updatedComps,
      };
      setBrandData({ ...brandData, categories: updated });
    } else {
      const updated = [...brandData.categories];

      const updatedComps = [
        ...updated[selectedCategoryIndex].competitors,
        competitor,
      ];

      updated[selectedCategoryIndex] = {
        ...updated[selectedCategoryIndex],
        competitors: updatedComps,
      };
      setBrandData({ ...brandData, categories: updated });
    }
  };

  // Define the fetch function
  const updateCategoryBrandPillarsAndPurchaseCriterias = async () => {
    try {
      const selected = coreAttributes.data.data.filter(
        (item) => item.is_default === 1
      );
      const selectedValues = selected?.map((option) => option?.name) || [];

      setBrandData((prev) => {
        const updated = [...prev.categories];
        updated[selectedCategoryIndex] = {
          ...updated[selectedCategoryIndex],
          purchaseCriteriaLoading: true,
          brandPillars: selected,
          brandPillarsLabels: selectedValues,
        };
        return { ...prev, categories: updated };
      });

      let res = await fetch(
        `http://${process.env.REACT_APP_BASE_URL}/metrics/generate-category-dimensions`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          method: "POST",
          body: JSON.stringify({
            llm: "gpt-4o",
            brand: brandName,
            category: brandData.categories?.[selectedCategoryIndex].name,
          }),
        }
      );
      res = await res.json();
      res = res.map((item) => item.name);

      setBrandData((prev) => {
        const updated = [...prev.categories];
        updated[selectedCategoryIndex] = {
          ...updated[selectedCategoryIndex],
          purchaseCriteria: res,
          purchaseCriteriaLoading: false,
          brandPillars: selected,
          brandPillarsLabels: selectedValues,
        };
        return { ...prev, categories: updated };
      });
    } catch (error) {
      console.error("Error fetching purchase criteria:", error);

      setBrandData((prev) => {
        const updated = [...prev.categories];
        updated[selectedCategoryIndex] = {
          ...updated[selectedCategoryIndex],
          purchaseCriteriaLoading: false,
        };
        return { ...prev, categories: updated };
      });
    }
  };

  // Trigger the debounce function when brandName or brandCategoryName changes
  useEffect(() => {
    if (brandName && brandData?.categories?.[selectedCategoryIndex]?.name) {
      updateCategoryBrandPillarsAndPurchaseCriterias();
    }
  }, [brandName, brandData.categories.length]);

  async function getCompetitorsData(type) {
    if (
      type === "ai" &&
      brandName &&
      (prevBrandName !== brandName ||
        !currentCategories.includes(
          brandData.categories?.[selectedCategoryIndex]?.name
        ))
    ) {
      let prompt = getPromptForBrandCompetitorsData(
        brandName,
        undefined,
        brandData.categories?.[selectedCategoryIndex]?.name
      );

      // 🔄 Set loading = true safely
      setBrandData((prev) => {
        const updated = [...prev.categories];
        updated[selectedCategoryIndex] = {
          ...updated[selectedCategoryIndex],
          competitorsLoading: true,
        };
        return { ...prev, categories: updated };
      });

      try {
        let result = await fetchGpt4API(
          prompt,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          "Brand-onboarding"
        );
        result = result["GPT-4"][0];

        let matchedArray = result.match(
          /\[\s*\{[^\}]*\}\s*(,\s*\{[^\}]*\}\s*)*\]/g
        );

        if (matchedArray) {
          let resultArray = json5.parse(matchedArray[0]);

          // 🔄 Set competitorsFromAi + loading = false safely
          setBrandData((prev) => {
            const updated = [...prev.categories];
            updated[selectedCategoryIndex] = {
              ...updated[selectedCategoryIndex],
              competitorsLoading: false,
              competitorsFromAi: resultArray,
            };
            return { ...prev, categories: updated };
          });

          setPrevBrandName(brandName);
          setCurrentCategories((prev) => [
            ...new Set([
              ...prev,
              brandData.categories?.[selectedCategoryIndex]?.name,
            ]),
          ]);
        }
      } catch (err) {
        // 🔄 Set loading = false safely on error
        setBrandData((prev) => {
          const updated = [...prev.categories];
          updated[selectedCategoryIndex] = {
            ...updated[selectedCategoryIndex],
            competitorsLoading: false,
          };
          return { ...prev, categories: updated };
        });
      }
    }
  }

  const handleDoubleClick = (index, value) => {
    const updated = [...brandData.categories];
    updated[selectedCategoryIndex] = {
      ...updated[selectedCategoryIndex],
      editedText: value,
      editingIndex: index,
    };
    setBrandData({ ...brandData, categories: updated });
  };

  const handleInputChange = (event) => {
    const updated = [...brandData.categories];
    updated[selectedCategoryIndex] = {
      ...updated[selectedCategoryIndex],
      editedText: event.target.value,
    };

    setBrandData({ ...brandData, categories: updated });
  };

  const handleBlur = () => {
    if (
      brandData.categories?.[selectedCategoryIndex].editedText &&
      brandData.categories?.[selectedCategoryIndex].editingIndex !== null
    ) {
      const updated = [...brandData.categories];

      const updatedPurchaseCriteria = [
        ...updated[selectedCategoryIndex].purchaseCriteria,
      ];

      updatedPurchaseCriteria[updated[selectedCategoryIndex].editingIndex] =
        updated[selectedCategoryIndex].editedText.trim();
      updated[selectedCategoryIndex] = {
        ...updated[selectedCategoryIndex],
        purchaseCriteria: updatedPurchaseCriteria,
        editingIndex: null,
      };
      setBrandData({ ...brandData, categories: updated });
    } else {
      const updated = [...brandData.categories];
      updated[selectedCategoryIndex] = {
        ...updated[selectedCategoryIndex],
        editingIndex: null,
      };
      setBrandData({ ...brandData, categories: updated });
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleBlur();
    }
  };

  const processInputData = (data) => {
    const modifiedresults = data?.categories?.map((obj) => {
      return {
        category_name: obj?.name,
        category_description: obj?.description,
        comp_brands: obj?.competitors,
        personaIds: obj?.persona?.map((item) => item?.persona_id),
        coreAttributesList: obj?.brandPillars?.map((obj) => {
          return { id: obj?.id };
        }),
        brandCategoryDimensions: obj?.purchaseCriteria,
      };
    });

    return { brand_name: brandName, categories: modifiedresults };
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    const input = processInputData(brandData);

    const hasTooManyCompBrands = input?.categories?.some(
      (category) => category?.comp_brands?.length > 4
    );

    if (hasTooManyCompBrands) {
      alert("You can only select up to 4 competitors.");
      return;
    }

    if (isUpdateModalOpen) {
      updateCreatedBrand(input);
    } else {
      createNewBrand(input);
    }
  };

  const handlePersonaCheckChange = (selected) => {
    const selectedValues =
      selected?.map((option) => option?.persona?.name) || [];

    // Update category-specific persona and personaLabel
    const updated = [...brandData.categories];
    updated[selectedCategoryIndex] = {
      ...updated[selectedCategoryIndex],
      persona: selected,
      personaLabel: selectedValues,
    };

    setBrandData({ ...brandData, categories: updated });
  };

  const handleSelectedCoreAttributes = (selected) => {
    if (selected?.length > 8) {
      console.table(selected);
      alert("You can select maximum 8 brand pillars");
      return;
    }
    const selectedValues = selected?.map((option) => option?.name) || [];
    // Update category-specific brand pillars and labels
    const updated = [...brandData.categories];
    updated[selectedCategoryIndex] = {
      ...updated[selectedCategoryIndex],
      brandPillars: selected,
      brandPillarsLabels: selectedValues,
    };
    setBrandData({ ...brandData, categories: updated });
  };

  const isValid =
    Array.isArray(brandData.categories) &&
    brandData.categories.length > 0 &&
    brandData.categories.every((category) =>
      ["persona", "competitors", "purchaseCriteria", "brandPillars"].every(
        (key) => Array.isArray(category[key]) && category[key].length > 0
      )
    );

  return (
    <form onSubmit={handleFormSubmit}>
      <div className={classes.modalBody}>
        <div>
          <TextInput
            label={`${ENTITY_LABELS.brand} Name`}
            variant="outlined"
            fullWidth
            required
            value={brandName}
            onChange={(e) => {
              setBrandName(e.target.value);
            }}
            className={classes.textField}
          />
        </div>
        <p></p>
        <Box style={{ display: "flex", alignItems: "center" }} gap={2}>
          <TextInput
            className={classes.textField}
            label="Category Name"
            value={newCategory.name}
            onChange={(e) =>
              setNewCategory({ ...newCategory, name: e.target.value })
            }
          />
          <TextInput
            className={classes.textField}
            label="Category Description"
            value={newCategory.description}
            onChange={(e) =>
              setNewCategory({ ...newCategory, description: e.target.value })
            }
          />
          <Button
            style={{ height: "35px", marginTop: "17px" }}
            variant="contained"
            disabled={
              brandData?.categories?.[selectedCategoryIndex]
                ?.purchaseCriteriaLoading ||
              brandData?.categories?.[selectedCategoryIndex]
                ?.competitorsLoading ||
              !brandName ||
              !newCategory?.name ||
              !newCategory?.description
            }
            onClick={() => {
              if (newCategory.name.trim()) {
                setBrandData((prev) => ({
                  ...prev,
                  categories: [
                    ...prev.categories,
                    {
                      ...newCategory,
                      competitors: [],
                      competitorsLoading: false,
                      competitorsFromAi: [],
                      competitorsSource: "manual",
                      purchaseCriteria: [],
                      purchaseCriteriaLoading: false,
                      brandPillars: [],
                      brandPillarsLabels: [],
                      persona: [],
                      personaLabel: [],
                      editingIndex: null,
                      editedText: "",
                    },
                  ],
                }));
                setNewCategory({ name: "", description: "" });
                setSelectedCategoryIndex(brandData?.categories?.length);
              }
            }}
          >
            +
          </Button>
        </Box>

        <Tabs
          value={selectedCategoryIndex}
          onChange={(_, newIndex) => {
            const current = brandData.categories[selectedCategoryIndex];
            const target = brandData.categories[newIndex];

            if (
              current.purchaseCriteriaLoading ||
              current.competitorsLoading ||
              target.purchaseCriteriaLoading ||
              target.competitorsLoading
            ) {
              // Prevent switching
              return;
            }

            setSelectedCategoryIndex(newIndex);
          }}
        >
          {brandData.categories.map((cat, index) => (
            <Tab key={index} label={cat.name} />
          ))}
        </Tabs>

        {brandData?.categories?.[selectedCategoryIndex] && (
          <>
            <Box className="mt-4">
              <InputLabel shrink htmlFor="input">
                {ENTITY_LABELS.brand} Category Competitor
              </InputLabel>
              <RadioButtonsGroup
                key={
                  brandData.categories?.[selectedCategoryIndex]
                    ?.competitorsSource || "default"
                }
                options={radioOption}
                selectedvalue={
                  brandData.categories?.[selectedCategoryIndex]
                    ?.competitorsSource
                }
                onChange={(v) => {
                  const updated = [...brandData.categories];
                  updated[selectedCategoryIndex] = {
                    ...updated[selectedCategoryIndex],
                    competitorsSource: v,
                    competitors: [],
                  };

                  setBrandData({ ...brandData, categories: updated });
                  getCompetitorsData(v);
                }}
              ></RadioButtonsGroup>
            </Box>

            <Box className="mt-2">
              <TextInput
                label={`Competitors ${ENTITY_LABELS.brand} Description`}
                variant="outlined"
                fullWidth
                margin="normal"
                required
                value={brandData.categories[
                  selectedCategoryIndex
                ].competitors?.join(", ")}
                onChange={(e) => {
                  const updated = [...brandData.categories];
                  updated[selectedCategoryIndex].competitors = e.target.value
                    .split(",")
                    .map((val) => val.trim());
                  setBrandData({ ...brandData, categories: updated });
                }}
                // className={classes.select}
              />
              <Typography
                color="textDisabled"
                variant="caption"
                sx={{ fontSize: "8px" }}
              >
                Please add multiple inputs separated by comma (max 4)
              </Typography>
            </Box>
            {brandData.categories?.[selectedCategoryIndex]
              ?.competitorsLoading && <LinearProgress />}
            <Box className="mt-1">
              {
                // brandData.categories?.[selectedCategoryIndex]
                //   ?.competitorsSource === "ai" &&
                brandData.categories?.[
                  selectedCategoryIndex
                ]?.competitorsFromAi?.map((competitor, index) => (
                  <Chip
                    style={{ margin: "5px", borderRadius: "3px" }}
                    key={competitor}
                    label={competitor.comp_brand_name}
                    onClick={() =>
                      handleCompetitorToggle(competitor.comp_brand_name)
                    }
                    color={
                      brandData.categories?.[
                        selectedCategoryIndex
                      ]?.competitors?.includes(competitor.comp_brand_name)
                        ? "primary"
                        : "default"
                    }
                  />
                ))
              }
            </Box>

            {!isUpdateModalOpen && (
              <Box mt={2}>
                <InputLabel shrink htmlFor="input">
                  {ENTITY_LABELS.purchaseCriteria}
                </InputLabel>
                {brandData.categories?.[selectedCategoryIndex]
                  .purchaseCriteriaLoading && (
                  <LinearProgress className="mb-2" />
                )}
                {brandData.categories?.[selectedCategoryIndex]?.purchaseCriteria
                  ?.length === 0 && (
                  <Typography color="error" variant="caption" mt={1}>
                    Please enter brand name and brand category.
                  </Typography>
                )}
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {brandData.categories?.[
                    selectedCategoryIndex
                  ].purchaseCriteria.map((tag, index) => (
                    <Box key={index}>
                      {brandData.categories?.[selectedCategoryIndex]
                        .editingIndex === index ? (
                        <TextField
                          value={
                            brandData.categories?.[selectedCategoryIndex]
                              .editedText
                          }
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          onKeyPress={handleKeyPress}
                          size="small"
                          variant="outlined"
                          autoFocus
                        />
                      ) : (
                        <Chip
                          label={tag}
                          color="primary"
                          variant="outlined"
                          title="Double click to edit"
                          onDoubleClick={() => handleDoubleClick(index, tag)}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {coreAttributes?.data && (
              <Box mt={2}>
                <InputLabel shrink htmlFor="input">
                  {ENTITY_LABELS.brandPillar}
                </InputLabel>
                <InputSelectionComponent
                  selectedOptions={
                    brandData?.categories?.[selectedCategoryIndex]
                      ?.brandPillarsLabels
                  }
                  handleChange={handleSelectedCoreAttributes}
                  options={coreAttributes.data.data.map((val) => {
                    return { ...val, type: "coreAttributes" };
                  })}
                  type="coreAttributes"
                  required
                />
                {brandData?.categories?.[selectedCategoryIndex]
                  ?.brandPillarsLabels?.length < 8 && (
                  <Typography color="error" variant="caption" mt={1}>
                    Please select exactly 8 {ENTITY_LABELS.brandPillar}.
                  </Typography>
                )}
              </Box>
            )}
            <Box mt={2}>
              <InputLabel shrink htmlFor="input">
                Persona
              </InputLabel>
              <InputSelectionComponent
                selectedOptions={
                  brandData?.categories?.[selectedCategoryIndex]?.personaLabel
                }
                handleChange={handlePersonaCheckChange}
                options={personasData}
                type="persona"
              />
              {brandData?.categories?.[selectedCategoryIndex]?.personaLabel
                ?.length === 0 && (
                <Typography color="error" variant="caption" mt={1}>
                  Please select a persona.
                </Typography>
              )}
            </Box>
          </>
        )}

        <div className={classes.buttonDiv}>
          <Button type="submit" variant="contained" disabled={!isValid}>
            <div>{isUpdateModalOpen ? "Update" : "Add"}</div>
          </Button>
          <Button variant="outlined" onClick={handleCloseModal}>
            Cancel
          </Button>
        </div>
      </div>
    </form>
  );
};

export default BrandForm;
